<template>
    <div>
        <page-title :title="$t('institution.title')" :showTitle="false"></page-title>
        <div class="card col-12 mx-auto mt-2 px-0">
            <div class="card-header bg-primary text-white">
                <div class="card-title h5 m-0">{{ $t('institution.title') }}</div>
            </div>

            <div class="card-header" v-if="showSettingsBtn && showNewButton">
                <button type="button" class="btn btn-primary btn-lg"
                    v-if="showNewButton && claims.institutionsRegister.create"
                    @click="goToCreate">
                    {{$t('buttons.create.btnText')}}
                </button>

                <button type="button"
                        class="btn btn-primary btn-lg"
                        v-if="showExportTemplateButton && claims.institutionsRegister.read"
                        @click="goToExportTemplate">
                    {{$t('buttons.exportTemplate.btnText')}}
                </button>
                <label v-if="showExportTemplateButton && claims.institutionsRegister.read">
                    <input type="checkbox" id="exportWithData" class="checkbox" />
                    <span>{{$t('checkboxes.exportWithData.cbText')}}</span>
                </label>

                <form v-if="claims.institutionsRegister.create" enctype="multipart/form-data" method="post" id="importForm" class="inlineForm">
                    <div class="inlineForm">
                        <button type="button"
                                class="btn btn-primary btn-lg"
                                v-if="showImportFromExcelButton"
                                @click="selectImportFiles">
                            {{$t('buttons.importFromExcel.btnText')}}
                        </button>
                        <input type="file" id="importFile" class="selectFile" @change="goToImportFromExcel" />
                    </div>
                </form>

                <i class="fas fa-cogs pointer fa-2x float-right text-success"
                   v-if="showSettingsBtn"
                   @click.stop="changeGridSettings"
                   data-toggle="tooltip"
                   :title="settingsTooltipText"></i>

                <grid-user-settings ref="userSettings"
                                    :schemaModelFields="schemaModelFields"
                                    :componentName="componentName"
                                    :useLabelAsTitle="true"
                                    :showUserSettings="true"
                                    :gridOptions="userSettings"
                                    v-if="showUserSettingsForm"
                                    @saveGridSettings="saveGridSettings"
                                    @loadGridSettings="loadGridSettings"
                                    @deleteGridSettings="deleteGridSettings"
                                    @userSettingsChanged="userSettingsChanged"></grid-user-settings>
            </div>

            <div class="card-body">
                <div class="btn-toolbar justify-content-between mt-0 mb-2" role="toolbar">
                    <div class="form-inline col-6"></div>

                    <div class="input-group">
                        <input v-model="searchQuery"
                            type="text"
                            v-on:keyup.enter.stop="massSearch"
                            v-on:keyup.esc.stop="clearFilters"
                            class="form-control"
                            aria-label="Mass search input"
                            aria-describedby="massSearch"
                        />
                        <button class="btn btn-secondary" @click.stop="massSearch">
                            <i class="fas fa-search"></i>
                            {{ $t('buttons.search.btnText') }}
                        </button>
                        <button class="btn btn-secondary" @click.stop="clearFilters">
                            <i class="fas fa-times"></i>
                            {{ $t('buttons.clearFilters.btnText') }}
                        </button>
                    </div>
                </div>

                <kendo-datasource ref="datasource1"
                                  :transport="transportSettings"
                                  :schema-model-id="'id'"
                                  :schema-model-fields="schemaModelFields"
                                  :schema-total="'total'"
                                  :schema-data="'data'"
                                  :page-size="defaultGridRows"
                                  :server-paging="true"
                                  :server-sorting="true"
                                  :server-filtering="true"
                                  :error="dataSourceError"></kendo-datasource>

                <kendo-grid ref="grid"
                            :data-source-ref="'datasource1'"
                            :editable="gridOptions.editable"
                            :filterable="gridOptions.filterable"
                            :pageable="gridOptions.pageable"
                            :reorderable="gridOptions.reorderable"
                            :resizable="gridOptions.resizable"
                            :groupable="gridOptions.groupable"
                            :allow-copy="gridOptions.allowCopy"
                            :column-menu="gridOptions.columnMenu"
                            :sortable-mode="gridOptions.sortable.mode"
                            :sortable-allow-unsort="gridOptions.sortable.allowUnsort"
                            :sortable-show-indexes="gridOptions.sortable.showIndexes"
                            :selectable="gridOptions.selectable"
                            :scrollable="gridOptions.scrollable"
                            :toolbar="toolbar"
                            :excel="gridOptions.excel"
                            :dataBound="dataBound">
                    <kendo-grid-column :template="btnsTemplate"
                                       field="btnsColumn"
                                       :title="$t('actions')"
                                       :reorderable="false"
                                       :sortable="false"
                                       :filterable="false"
                                       :lockable="true"
                                       :locked="false"
                                       :width="150"
                                       v-if="showBtns"></kendo-grid-column>

                    <kendo-grid-column :field="'id'" :title="$t('institution.id')" :width="100" :lockable="false" :locked="false" :hidden="true"></kendo-grid-column>

                    <kendo-grid-column :field="'name'" :width="300" :lockable="true" :locked="false" :hidden="isEnVersion" :title="$t('institution.name')"></kendo-grid-column>
                    <kendo-grid-column :field="'nameEn'" :width="300" :lockable="true" :locked="false" :hidden="!isEnVersion" :title="$t('institution.nameEn')"></kendo-grid-column>
                    <kendo-grid-column :field="'city'" :width="200" :lockable="true" :locked="false" :hidden="isEnVersion" :title="$t('address.settlement')"></kendo-grid-column>
                    <kendo-grid-column :field="'cityEn'" :width="200" :lockable="true" :locked="false" :hidden="!isEnVersion" :title="$t('address.settlementEn')"></kendo-grid-column>
                    <kendo-grid-column :field="'level'" :width="200" :lockable="true" :locked="false" :title="$t('institution.level')"></kendo-grid-column>
                    <kendo-grid-column :field="'type'" :width="250" :lockable="true" :locked="false" :title="$t('institution.type')"></kendo-grid-column>
                    <kendo-grid-column :field="'country'" :width="200" :lockable="true" :locked="false" :hidden="isEnVersion" :title="$t('address.country')"></kendo-grid-column>
                    <kendo-grid-column :field="'countryEn'" :width="200" :lockable="true" :locked="false" :hidden="!isEnVersion" :title="$t('address.countryEn')"></kendo-grid-column>
                    <kendo-grid-column :field="'appellateRegion'"
                                       :width="200"
                                       :lockable="true"
                                       :locked="false"
                                       :hidden="isEnVersion"
                                       :title="$t('institution.appellateRegion')"></kendo-grid-column>
                    <kendo-grid-column :field="'appellateRegionEn'"
                                       :width="200"
                                       :lockable="true"
                                       :locked="false"
                                       :hidden="!isEnVersion"
                                       :title="$t('institution.appellateRegionEn')"></kendo-grid-column>
                    <kendo-grid-column :field="'active'"
                                       :template="activeCellTemplate"
                                       :width="120"
                                       :lockable="true"
                                       :locked="false"
                                       :title="$t('institution.active')"></kendo-grid-column>
                </kendo-grid>
            </div>
        </div>
        <JsonFormatter v-model="dataReferences" :show.sync="showDataReferences"
                       :titleKey="'buttons.links.tooltipText'"
                       @onClose="onDataReferencesClose">
        </JsonFormatter>

        <ReplacementHistoryModal
            v-model="replacementHistoryModel"
            :show.sync="showReplacementHistory"
            @onClose="onReplacementHistoryClose" />
    </div>
</template>

<script src="https://kendo.cdn.telerik.com/2019.2.619/js/pako_deflate.min.js"></script>

<script>
    import Vue from 'vue';
    import JSZip from 'jszip';
    import store from '../../../store/store';
    import GridUserSettings from '../../../components/grid/UserSettings';
    import BtnsTemplate from '../../../components/grid/StandardBtnsTemplate';
    import PageTitle from '../../../components/common/PageTitle';
    import { Culture } from '../../../models/enums';
    import personalizationService from '../../../services/personalization.service';
    import { mapGetters } from 'vuex';
    import dataReferencesService from '@/services/dataReferences.service';
    import JsonFormatter from "@/components/common/json.formatter.modal.vue";
    import ReplacementHistoryModal from "@/components/administration/dataReferences/replacement.history.modal";

    /*
        Renders the grid in "DejaVu Sans" font family, which is
        declared in kendo.common.css. It also declares the paths to the
        fonts below using <tt>kendo.pdf.defineFont</tt>, because the
        stylesheet is hosted on a different domain.
        */
    kendo.pdf.defineFont({
        'DejaVu Sans': 'https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans.ttf',
        'DejaVu Sans|Bold': 'https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans-Bold.ttf',
        'DejaVu Sans|Bold|Italic': 'https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans-Oblique.ttf',
        'DejaVu Sans|Italic': 'https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans-Oblique.ttf',
        WebComponentsIcons: 'https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/glyphs/WebComponentsIcons.ttf'
    });

    export default {
        components: {
            PageTitle,
            'grid-user-settings': GridUserSettings,
            JsonFormatter,
            ReplacementHistoryModal
        },
        props: {
            showHideButton: {
                default: false,
                type: Boolean
            },
            showPinButton: {
                default: true,
                type: Boolean
            },
            showNewButton: {
                default: true,
                type: Boolean
            },
            showExportTemplateButton: {
                default: true,
                type: Boolean
            },
            showImportFromExcelButton: {
                default: true,
                type: Boolean
            },
            showResizeIcon: {
                default: false,
                type: Boolean
            },
            size: {
                default: 6,
                type: Number
            },
            showBtns: {
                default: true,
                type: Boolean
            },
            showSettingsBtn: {
                default: true,
                type: Boolean
            }
        },
        data() {
            return {
                componentName: 'InstitutionRegisterGrid',
                settingsTooltipText: this.$t('buttons.settings.tooltipText'),
                customSettingsLoaded: false,
                titleKey: 'institution.title',
                defaultGridRows: this.$store.getters.defaultGridRows,
                searchQuery: '',
                schemaModelFields: {
                    id: {
                        name: 'id',
                        type: 'number',
                        editable: false,
                        nullable: false,
                        visible: true,
                        titleKey: 'institution.id'
                    },
                    name: {
                        name: 'name',
                        type: 'string',
                        editable: false,
                        nullable: false,
                        visible: true,
                        titleKey: 'institution.name'
                    },
                    nameEn: {
                        name: 'nameEn',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'institution.nameEn'
                    },
                    level: {
                        name: 'level',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'institution.level'
                    },
                    type: {
                        name: 'type',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'institution.type'
                    },
                    appellateRegion: {
                        name: 'appellateRegion',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'institution.appellateRegion'
                    },
                    appellateRegionEn: {
                        name: 'appellateRegionEn',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'institution.appellateRegionEn'
                    },
                    country: {
                        name: 'country',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'address.country'
                    },
                    countryEn: {
                        name: 'countryEn',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'address.countryEn'
                    },
                    city: {
                        name: 'city',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'address.settlement'
                    },
                    cityEn: {
                        name: 'cityEn',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'address.settlementEn'
                    },
                    active: {
                        name: 'active',
                        type: 'boolean',
                        editable: false,
                        nullable: false,
                        visible: true,
                        titleKey: 'institution.active'
                    }
                },
                rawHtml: '',
                transportSettings: {
                    read: {
                        url: `${store.getters.baseUrl}/api/institution/list`,
                        dataType: 'json',
                        type: 'POST',
                        contentType: 'application/json',
                        beforeSend: this.beforeSend
                    },
                    parameterMap: this.parameterMap
                },
                gridOptions: {
                    ...this.$store.getters.gridOptions,
                    editable: {
                        mode: 'popup' /*inline, popup*/,
                        window: {
                            title: this.$t('person.gridTitle'),
                            animation: true
                        },
                        move: true
                    },
                    excel: {
                        fileName: 'Institutions.xlsx',
                        allPages: false,
                        filterable: true,
                        collapsible: true
                    },
                    pdf: {
                        fileName: 'Institutions.pdf',
                        title: this.$t(this.titleKey),
                        subject: this.$t(this.titleKey),
                        creator: this.$store.userName,
                        allPages: false,
                        avoidLinks: true,
                        paperSize: 'A4',
                        margin: { top: '2cm', left: '1cm', right: '1cm', bottom: '1cm' },
                        landscape: true,
                        repeatHeaders: true,
                        scale: 0.6
                    }
                },
                working: false,
                showUserSettingsForm: false,
                dataReferences: '',
                showDataReferences: false,
                showReplacementHistory: false,
                replacementHistoryModel: null,
            };
        },
        methods: {
            dataSourceError(e) {
                this.$vs.notify({
                    title: this.$t('notifications.error'),
                    text: this.$helpers.kendoDataSourceErrorText(e),
                    color: 'danger',
                    time: this.$store.getters.notificationDuration
                });
            },
            goToCreate() {
                this.$router.push('/registers/institution/create');
            },
            goToExportTemplate() {
                var $exportCheckbox = $('#exportWithData');
                var isChecked = $exportCheckbox.is(':checked');

                this.$http
                    .get('/api/institution/exportTemplate/' + isChecked, { responseType: 'blob' })
                    .then(response => {
                        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', 'Institutions.xlsx'); //or any other extension
                        document.body.appendChild(link);
                        link.click();
                    })
                    .catch(error => {
                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        });
                    });
            },
            selectImportFiles() {
                var dialog = $('#importFile');
                dialog.trigger('click');
            },
            goToImportFromExcel(args) {
                var $input = $('#importFile')[0];
                var files = $input.files;
                if (files && files.length > 0) {
                    var filename = files[0].name;
                    var file = files[0];

                    // clear the selected file so that the change event triggers when the same file is selected
                    $input.value = null;

                    var formData = new FormData();
                    formData.append('FileName', filename);
                    formData.append('FileContent', file);

                    this.$http
                        .post('/api/institution/import', formData)
                        .then(result => {
                            this.$refs.grid.kendoWidget().dataSource.read();
                            this.$vs.notify({
                                title: this.$t('notifications.success'),
                                text: result.data,
                                position: 'bottom-right',
                                color: 'success'
                            });
                        })
                        .catch(error => {
                            this.$vs.notify({
                                title: this.$t('notificationsPopup.registerImport.errorTitle'),
                                text: this.$t(error.response.data.msg) + (error.response.data.detailMsg || ''),
                                color: 'danger',
                                time: this.$store.getters.notificationDuration
                            });
                        });
                }
            },
            goToEdit(args) {
                this.$router.push(`/registers/institution/${args.id}/edit`);
            },
            goToDetails(args) {
                this.$router.push(`/registers/institution/${args.id}/details`);
            },
            pdfExport(args) {
                this.$http
                    .get('/api/institution/pdfExport/' + args.id, { responseType: 'blob' })
                    .then(response => {
                        const blob = new Blob([response.data], { type: 'application/pdf' });
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', 'Institution.pdf'); //or any other extension
                        document.body.appendChild(link);
                        link.click();
                    })
                    .catch(error => {
                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        });
                    });
            },
            excelExport(args) {
                this.$http
                    .get('/api/institution/excelExport/' + args.id, { responseType: 'blob' })
                    .then(response => {
                        const blob = new Blob([response.data], { type: 'application/vnd.ms-excel' });
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', 'Institution.xlsx'); //or any other extension
                        document.body.appendChild(link);
                        link.click();
                    })
                    .catch(error => {
                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        });
                    });
            },
            deleteBtnClick(args) {
                kendo.confirm(`${this.$t('buttons.delete.confirmationText1', { name: args.name })}`).then(
                    function () {
                        this.deleteInstitution(args.id);
                    }.bind(this)
                );
            },
            activeCellTemplate: function (dataItem) {
                return '<span class="badge ' + (dataItem.active ? 'badge-success' : 'badge-danger') + '">'
                    + kendo.htmlEncode(dataItem.active ? this.$t('yes') : this.$t('no'))
                    + '</span>';
            },
            deactivateBtnClick(args) {
                if (!args.active) {
                    this.$vs.notify({
                        title: this.$t('buttons.deactivate.action'),
                        text: this.$t('buttons.deactivate.already'),
                        color: 'warning',
                        time: this.$store.getters.notificationDuration
                    });

                    return;
                }

                this.toggleActivation(args.id, 'deactivate');
            },
            activateBtnClick(args) {
                if (args.active) {
                    this.$vs.notify({
                        title: this.$t('buttons.activate.action'),
                        text: this.$t('buttons.activate.already'),
                        color: 'warning',
                        time: this.$store.getters.notificationDuration
                    });

                    return;
                }

                this.toggleActivation(args.id, 'activate');
            },
            toggleActivation(id, action) {
                this.working = true;

                this.$http
                    .delete(`/api/institution/${action}`, { params: { id: id } })
                    .then(response => {
                        this.working = false;
                        this.refresh();

                        this.$vs.notify({
                            title: this.$t(`buttons.${action}.action`),
                            text: this.$helpers.responseMessage(response),
                            color: 'success',
                            time: this.$store.getters.notificationDuration
                        });
                    })
                    .catch(error => {
                        this.working = false;

                        this.$vs.notify({
                            title: this.$t(`buttons.${action}.action`),
                            text: this.$helpers.errorText(error),
                            color: this.$helpers.errorColor(error),
                            time: this.$store.getters.notificationDuration
                        });
                    });
            },
            dataReferencesBtnClick(args) {
                if (!this.claims.dataReferences.read) {
                    return this.$router.push(`/unauthorized`);
                }

                return this.getDataReferences(args.id);
            },
            deleteInstitution(id) {
                this.$http
                    .delete('/api/institution/', { params: { id: id } })
                    .then(response => {
                        this.refresh();
                        let statusCode = Number(this.$helpers.responseStatusCode(response));
                        this.$vs.notify({
                            title: this.$t('buttons.delete.action'),
                            text: this.$helpers.responseMessage(response),
                            color: statusCode === 240 ? 'warning' : 'success',
                            time: this.$store.getters.notificationDuration
                        });
                    })
                    .catch(error => {
                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        });
                    });
            },
            refresh() {
                let gr = this.$refs.grid;
                if (gr) {
                    gr.kendoWidget().dataSource.read();
                }
            },
            dataBound(e) {
                //This is needed for grid state save
                if (!this.customSettingsLoaded) {
                    this.loadGridSettings();
                }
            },
            parameterMap: function (options, operation) {
                if (operation !== 'read' && options.models) {
                    return { models: kendo.stringify(options.models) };
                } else {
                    //this.saveState(); //This is needed for grid state save
                    return kendo.stringify(options);
                }
            },

            //This is needed for grid state save and mass search
            getGridOptions() {
                const grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    return grid.getOptions();
                }
            },
            saveGridSettings() {
                const gridSettings = this.getGridOptions();
                const grid = this.$refs.grid.kendoWidget();
                if (gridSettings && grid) {
                    this.$refs.userSettings.setGridSettings(gridSettings);

                    personalizationService
                        .saveGridSettings(this.componentName, gridSettings)
                        .then(response => {
                            this.$vs.notify({
                                title: this.$t('request.response.status.' + this.$helpers.responseStatusCode(response)),
                                text: this.$helpers.responseMessage(response),
                                color: 'success',
                                time: this.$store.getters.notificationDuration
                            });
                        })
                        .catch(error => {
                            this.$vs.notify({
                                title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                                text: this.$helpers.errorText(error),
                                color: this.$helpers.errorColor(error),
                                time: this.$store.getters.notificationDuration
                            });
                        });
                }
            },
            loadGridSettings() {
                personalizationService
                    .loadGridSettings(this.componentName)
                    .then(response => {
                        try {
                            var options = personalizationService.mergePersonGridSettings(this.getGridOptions(), response.data.settings);

                            let grid = this.$refs.grid.kendoWidget();
                            if (options && grid) {
                                grid.setOptions(options);

                                if (options.pageable.size != grid.dataSource.options.pageSize) {
                                    grid.dataSource.pageSize(options.pageable.size);
                                    grid.refresh();
                                }

                                const userSettingsEditor = this.$refs.userSettings;
                                if (userSettingsEditor) {
                                    userSettingsEditor.setGridSettings(grid.getOptions());
                                }

                                //this.$vs.notify({
                                //    title: '',
                                //    text: this.$t('personalSettingsLoaded'),
                                //    color: 'success',
                                //    time: this.$store.getters.notificationDuration,
                                //})
                            }

                            this.customSettingsLoaded = true;
                        } catch (e) {
                            this.customSettingsLoaded = true;
                        }
                    })
                    .catch(error => {
                        this.customSettingsLoaded = true;

                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        });
                    });
            },
            deleteGridSettings() {
                personalizationService
                    .delteGridSettings(this.componentName)
                    .then(response => {
                        this.$router.go();
                    })
                    .catch(error => {
                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: this.$helpers.errorColor(error),
                            time: this.$store.getters.notificationDuration
                        });
                    });
            },
            userSettingsChanged(userSettings, pageSizeChanged) {
                let grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    try {
                        grid.setOptions(userSettings);
                        if (pageSizeChanged === true) {
                            grid.dataSource.pageSize(userSettings.pageable.size);
                            grid.refresh();
                        }
                    } catch (e) {
                        return;
                    }
                }
            },
            saveState() {
                personalizationService.saveState(this.componentName, this.getGridOptions());
            },
            loadState: function () {
                let stateSettings = personalizationService.getState(this.componentName);
                let options = personalizationService.mergeStateSettings(this.getGridOptions(), stateSettings);
                if (options) {
                    let grid = this.$refs.grid.kendoWidget();
                    if (grid) {
                        grid.setOptions(options);
                    }
                }
            },
            changeGridSettings() {
                this.showUserSettingsForm = !this.showUserSettingsForm;
            },
            clearFilters() {
                personalizationService.claerState(this.componentName);

                this.searchQuery = '';
                personalizationService.clearFilters(this.$refs.grid.kendoWidget());
            },
            massSearch() {
                if (!this.searchQuery) {
                    this.$vs.notify({
                        title: this.$t('buttons.search.btnText'),
                        text: this.$t('emptySearchFilter'),
                        color: 'warning',
                        time: this.$store.getters.notificationDuration
                    });

                    return;
                }

                let grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    let filter = this.$helpers.getMassFilterForGrid(grid, this.searchQuery);
                    if (filter) {
                        grid.dataSource.filter(filter);
                    }
                }
            },
            // End
            beforeSend: function (req) {
                if (this.token) {
                    req.setRequestHeader('Authorization', 'Bearer ' + this.token);
                }
            },
            btnsTemplate: function (e) {
                return {
                    template: Vue.component(BtnsTemplate.name, BtnsTemplate),
                    templateArgs: {
                        ...e,
                        parentComponent: this.$refs.grid,
                        btns: [
                            {
                                name: 'edit',
                                text: '',
                                tooltip: this.$t('buttons.edit.tooltipText'),
                                class: 'btn btn-success',
                                icon: 'fas fa-edit',
                                show: this.claims.institutionsRegister.update,
                                clickHandler: this.goToEdit
                            },
                            {
                                name: 'details',
                                text: '',
                                tooltip: this.$t('buttons.details.tooltipText'),
                                class: 'btn btn-info',
                                icon: 'fas fa-eye',
                                show: this.claims.institutionsRegister.read,
                                clickHandler: this.goToDetails
                            },
                            {
                                name: 'delete',
                                text: '',
                                tooltip: this.$t('buttons.delete.tooltipText'),
                                class: 'btn btn-danger',
                                icon: 'fas fa-times',
                                show: this.claims.institutionsRegister.delete,
                                clickHandler: this.deleteBtnClick
                            },
                            {
                                name: 'deactivation',
                                text: '',
                                tooltip: this.$t('buttons.deactivate.tooltipText'),
                                class: 'btn btn-danger',
                                icon: 'fas fa-lock',
                                show: this.claims.institutionsRegister.delete && e.active,
                                clickHandler: this.deactivateBtnClick
                            },
                            {
                                name: 'activation',
                                text: '',
                                tooltip: this.$t('buttons.activate.tooltipText'),
                                class: 'btn btn-success',
                                icon: 'fas fa-lock-open',
                                show: this.claims.institutionsRegister.delete && !e.active,
                                clickHandler: this.activateBtnClick
                            },
                            {
                                name: 'excelExport',
                                text: '',
                                tooltip: this.$t('buttons.excelExport.tooltipText'),
                                class: 'btn btn-success',
                                icon: 'far fa-file-excel',
                                show: this.claims.institutionsRegister.read,
                                clickHandler: this.excelExport
                            },
                            {
                                name: 'checkLinks',
                                text: '',
                                tooltip: this.$t('buttons.links.btnText'),
                                class: 'btn btn-info',
                                icon: 'fa fa-link',
                                show: this.claims.dataReferences.read,
                                clickHandler: this.dataReferencesBtnClick
                            },
                            {
                                name: 'referencesReplace',
                                text: '',
                                tooltip: this.$t('buttons.replace.btnText'),
                                class: 'btn btn-info',
                                icon: 'fas fa-sync',
                                show: this.claims.dataReferences.update,
                                clickHandler: this.dataReferencesReplaceCommandClick
                            },
                            {
                                name: 'replacementHistory',
                                text: '',
                                tooltip: this.$t('buttons.history.btnText'),
                                class: 'btn btn-info',
                                icon: 'fa fa-history',
                                show: this.claims.dataReferences.read,
                                clickHandler: this.replacementHistoryCommandClick
                            },
                        ]
                    }
                };
            },
            getDataReferences(id) {
                dataReferencesService
                    .checkReferences('Registers', 'Institution', id, this.dataReferencesListCount, true)
                    .then(response => {
                        if (response.data) {
                            this.dataReferences = response.data;
                        }

                        this.showDataReferences = true;
                    })
                    .catch(error => {
                        console.log(error);
                    });
            },
            onDataReferencesClose() {
                this.dataReferences = '';
                this.showDataReferences = false
            },
            onReplacementHistoryClose() {
                this.replacementHistoryModel = null;
                this.showReplacementHistory = false
            },
            dataReferencesReplaceCommandClick(args) {
                if (!this.claims.dataReferences.update) {
                    return this.$router.push(`/unauthorized`);
                }

                if (!args) {
                    return;
                }

                kendo.prompt(`${this.$t('dataReference.replacePrompt')}`, '')
                    .then(function (data) {
                        const replacementId = Number(data);
                        if (isNaN(replacementId)) {
                            this.$vs.notify({
                                    title: this.$t('notifications.error'),
                                    text: 'replacementId is not a number',
                                    color: 'danger',
                                    time: this.$store.getters.notificationDuration
                                });
                            return;
                        }

                        const model = {
                            schemaName: 'Registers',
                            tableName: 'Institution',
                            replacedId: args.id,
                            replacementId: replacementId
                        }
                        dataReferencesService.replaceReferences(model)
                            .then(response => {
                                this.$vs.notify({
                                    title: this.$t('buttons.replace.btnText'),
                                    text: this.$helpers.responseMessage(response),
                                    color: 'success',
                                    time: this.$store.getters.notificationDuration
                                });
                            })
                            .catch(error => {
                                this.$vs.notify({
                                    title: this.$t('buttons.replace.btnText'),
                                    text: this.$helpers.errorText(error),
                                    color: 'danger',
                                    time: this.$store.getters.notificationDuration
                                });
                            });
                    }.bind(this));
            },
            replacementHistoryCommandClick(args) {
                if (!this.claims.dataReferences.read) {
                    return this.$router.push(`/unauthorized`);
                }

                if (!args) {
                    return;
                }

                this.replacementHistoryModel = {
                    schemaName: 'Registers',
                    tableName: 'Institution',
                    entityId: args.id
                };
                this.showReplacementHistory = true;
            },
        },
        created: function () {
            window.JSZip = JSZip; // Необходимо е за да работи експорта в excel
        },
        mounted: function () {
            var container = $(this.$el);
            container.find('[data-toggle="tooltip"]').tooltip({ container });
        },
        computed: {
            ...mapGetters(['claims', 'dataReferencesListCount']),
            token: function () {
                return store.getters.authToken;
            },
            userSettings() {
                let grid = this.$refs.grid.kendoWidget();
                return grid.getOptions();
            },
            isEnVersion() {
                return this.$store.getters.currentCulture === Culture.EN;
            },
            toolbar: function () {
                return [{ name: 'excel', text: this.$t('buttons.excelExport.tooltipText') }];
            }
        },
        watch: {
            customSettingsLoaded: function (val) {
                if (val === true) {
                    // this.loadState();
                }
            }
        }
    };
</script>
<style scoped>
    .btn {
        margin-right: 10px;
        margin-left: 5px;
    }

    .checkbox {
        margin-right: 5px;
    }

    .selectFile {
        display: none;
    }

    .inlineForm {
        display: inline;
    }

    /*
        Use the DejaVu Sans font for display and embedding in the PDF file.
        The standard PDF fonts have no support for Unicode characters.
    */
    .k-pdf-export .k-grid {
        font-family: 'DejaVu Sans', 'Arial', sans-serif;
    }

    .k-pdf-export .k-filterable .k-grid-filter {
        display: none;
    }

    .k-pdf-export .k-with-icon .k-header-column-menu {
        display: none;
    }

    .k-grid .k-grid-header .k-header .k-link {
        height: auto;
    }

    .k-grid .k-grid-header .k-header {
        white-space: normal;
    }

    .k-grid-edit,
    .k-grid-details,
    .k-grid-delete,
    .k-grid-pdf,
    .k-grid-excel {
        padding: 0;
        margin-bottom: 2px;
    }
</style>


